/** audio-agent Gateway 7.0 的浏览器 WebSocket 边界。 */

import type { VoiceMode } from "../config";

export type VoiceGatewayEvent = Record<string, unknown>;

export interface VoiceGatewayCallbacks {
  onEvent: (event: VoiceGatewayEvent) => void;
  onClose: (code: number, reason: string) => void;
  onError: (error: unknown) => void;
}

const PROTOCOL_VERSION = "7.0.0";
const OCCUPIED_CLOSE = 4002;
const REPLACED_CLOSE = 4001;
const REVOKED_CLOSE = 4003;
export const SHOPPER_SESSION_ENDED_CLOSE = 4004;

// Gateway 在每次语音提问时通过 Client Action 拉取当前页面上下文。
export const SHOP_PAGE_CONTEXT_ACTION = "shop.page_context.read";
export const SHOP_PAGE_CONTEXT_CAPABILITY = `client.actions.${SHOP_PAGE_CONTEXT_ACTION}`;

export const VOICE_AUDIO_SUBPROTOCOL = "qwaudio.gcp.v6";
export const VOICE_SHOPPER_SUBPROTOCOL = "gonovus.shopper.";
export const VOICE_CONVERSATION_SUBPROTOCOL = "gonovus.conversation.";

export const VOICE_TERMINAL_CLOSE_CODES = new Set([
  OCCUPIED_CLOSE,
  REPLACED_CLOSE,
  REVOKED_CLOSE,
  SHOPPER_SESSION_ENDED_CLOSE,
]);

function eventId(prefix: string): string {
  let random: string | undefined;
  try {
    random = globalThis.crypto.randomUUID();
  } catch {
    random = undefined;
  }
  return `${prefix}_${random ?? `${String(Date.now())}_${Math.random().toString(16).slice(2)}`}`;
}

function readSocketData(data: unknown): Promise<string> {
  if (typeof data === "string") return Promise.resolve(data);
  if (data instanceof Blob) return data.text();
  if (data instanceof ArrayBuffer)
    return Promise.resolve(new TextDecoder().decode(data));
  return Promise.resolve(String(data));
}

export class VoiceGatewayConnection {
  private socket: WebSocket | undefined;
  private stopped = true;
  private readonly instanceId = eventId("widget");

  constructor(
    private readonly url: string,
    token: string,
    conversationId: string | undefined,
    private readonly voiceMode: VoiceMode | undefined,
    private readonly callbacks: VoiceGatewayCallbacks,
  ) {
    this.protocols = [
      VOICE_AUDIO_SUBPROTOCOL,
      `${VOICE_SHOPPER_SUBPROTOCOL}${token}`,
      ...(conversationId
        ? [`${VOICE_CONVERSATION_SUBPROTOCOL}${conversationId}`]
        : []),
    ];
  }

  private readonly protocols: string[];

  get webSocket(): WebSocket | undefined {
    return this.socket;
  }

  connect(): void {
    if (!this.stopped) return;
    this.stopped = false;
    const socket = new WebSocket(this.url, this.protocols);
    this.socket = socket;
    socket.addEventListener("open", () => {
      if (this.socket !== socket || this.stopped) return;
      this.send({
        type: "session.hello",
        event_id: eventId("evt_client"),
        protocol: { min: PROTOCOL_VERSION, max: PROTOCOL_VERSION },
        client: {
          type: "web",
          version: "0.1.0",
          instance_id: this.instanceId,
        },
        capabilities: [
          "input.audio",
          "playback.receipts",
          "session.heartbeat",
          SHOP_PAGE_CONTEXT_CAPABILITY,
        ],
        locale: navigator.language,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        connection: {
          voice_enabled: true,
          input_enabled: true,
          output_enabled: true,
          text_only: false,
          ...(this.voiceMode === undefined
            ? {}
            : {
                provider:
                  this.voiceMode === "live" ? "azure-live" : "azure-cascade",
              }),
        },
      });
    });
    socket.addEventListener("message", (message) => {
      if (this.socket !== socket || this.stopped) return;
      void readSocketData(message.data).then((raw) => {
        if (this.socket !== socket || this.stopped) return;
        try {
          const parsed: unknown = JSON.parse(raw);
          if (typeof parsed !== "object" || parsed === null) return;
          this.receive(parsed as VoiceGatewayEvent);
        } catch {
          this.callbacks.onError(new Error("Gateway returned invalid JSON"));
        }
      });
    });
    socket.addEventListener("error", (event) => {
      if (this.socket === socket && !this.stopped)
        this.callbacks.onError(event);
    });
    socket.addEventListener("close", (event) => {
      if (this.socket !== socket) return;
      this.socket = undefined;
      this.callbacks.onClose(event.code, event.reason);
    });
  }

  send(event: VoiceGatewayEvent): boolean {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    try {
      socket.send(JSON.stringify(event));
      return true;
    } catch {
      return false;
    }
  }

  stop(): void {
    this.stopped = true;
    const socket = this.socket;
    this.socket = undefined;
    if (socket && socket.readyState < WebSocket.CLOSING)
      socket.close(1000, "voice disabled");
  }

  private receive(event: VoiceGatewayEvent): void {
    if (event.type === "session.ping" && typeof event.event_id === "string") {
      this.send({
        type: "session.pong",
        event_id: eventId("evt_client"),
        request_event_id: event.event_id,
      });
      return;
    }
    this.callbacks.onEvent(event);
  }
}

export function gatewayCloseMessage(code: number): string {
  if (code === OCCUPIED_CLOSE) return "occupied";
  if (code === REPLACED_CLOSE) return "replaced";
  if (code === REVOKED_CLOSE) return "revoked";
  if (code === SHOPPER_SESSION_ENDED_CLOSE) return "shopper_session_ended";
  return "closed";
}

export function createVoiceEventId(): string {
  return eventId("evt_client");
}
